import { useState, useMemo } from "react";
import { Box, Stack, Typography, Divider, Snackbar, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useResponsive } from "../../../utils/mobileresponsive";
import { columns, historyColumns } from "../components/columns";
import { BonusCard, MotivationMessage } from "../components/AgentEarningsMotivation";
import PaginationNavigation from "../../ClientList/components/PaginationNavigation";
import ConfirmationModal from "../../../components/confirmation-modal";
import useFetchAgentLeadership from "../hooks/useFetchAgentLeadership";

const dataGridSx = {
	"& .MuiDataGrid-filler": { display: "none !important" },
	"& .MuiDataGrid-columnHeaders": { lineHeight: "normal" },
	"& .MuiDataGrid-columnHeaderTitle": {
		whiteSpace: "normal",
		lineHeight: "1.2",
		overflowWrap: "break-word",
		textAlign: "left",
		fontWeight: 700,
	},
};

export default function LeadershipBonusesShell({
	activeAgentId,
	activeAgentName,
	headerSlot = null,
	isAgent,
	loggedInAgentName = null,
	approveReject,
	approveRejectLoading,
}) {
	const { isMobile } = useResponsive();

	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [confirmation, setConfirmation] = useState({ open: false, action: null, row: null });
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

	const { rows, historyRows, totalEarnings, total, loading, refetch } = useFetchAgentLeadership({
		userId: activeAgentId,
		page: currentPage,
		limit: perPage,
	});

	const totalPages = Math.ceil(total / perPage);

	const historyRowsMapped = useMemo(() => {
		return historyRows.map((item, index) => ({
			id: item.id ?? index,
			milestone_id: item.id,
			agent_id: item.agent_id,
			bonus_amount: item.reward,
			milestone: item.target,
			paid_date: item.paid_date,
			status: item.status,
		}));
	}, [historyRows]);

	const handleNext = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
	const handlePrev = () => setCurrentPage((p) => Math.max(p - 1, 1));
	const handlePerPage = (value) => {
		setPerPage(value);
		setCurrentPage(1);
	};

	const handleApproveBonus = (row) => setConfirmation({ open: true, action: "approved", row });
	const handleRejectBonus = (row) => setConfirmation({ open: true, action: "rejected", row });
	const handleCloseConfirmation = () => setConfirmation({ open: false, action: null, row: null });

	const showSnackbar = (message, severity = "info") => setSnackbar({ open: true, message, severity });
	const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

	const handleConfirmAction = async () => {
		const { action, row } = confirmation;

		if (!approveReject) {
			console.error("approveReject handler was not provided to LeadershipBonusesShell");
			return;
		}

		try {
			const payload = {
				id: row.id,
				action: action,
			};

			const result = await approveReject(payload);

			if (!result?.success) {
				showSnackbar(result?.error || "Something went wrong. Try again later", "error");
				return;
			}

			showSnackbar(action === "approved" ? "Successfully approved milestone" : "Successfully rejected milestone", "success");

			refetch?.();
		} catch (err) {
			console.error(err);
			showSnackbar("Unexpected error occurred", "error");
		} finally {
			handleCloseConfirmation();
		}
	};

	const historyGridColumns = useMemo(
		() => historyColumns({ onApprove: handleApproveBonus, onReject: handleRejectBonus, onViewDetails: (row) => console.log(row), isAgent }),
		[isAgent]
	);

	return (
		<Box p={4}>
			<Stack direction={{ xs: "column", lg: "row" }} alignItems="center !important" justifyContent="space-between" spacing={2} px={3} mb={4}>
				<Typography
					variant={isMobile ? "h2" : "h4"}
					sx={{ color: "#1D54A2", letterSpacing: 4, fontWeight: "bold !important" }}
					textAlign={isMobile ? "center" : ""}
				>
					LEADERSHIP BONUSES
				</Typography>
				{headerSlot}
			</Stack>

			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				gap={2}
				mb={3}
				flexWrap="wrap"
				sx={{ border: "1px solid rgba(29,84,162,0.1)", borderRadius: "12px", boxShadow: "0 1px 8px rgba(29,84,162,0.06)" }}
			>
				<BonusCard earnings={totalEarnings} loggedInAgent={isAgent ? loggedInAgentName : activeAgentName} />
				<MotivationMessage rows={rows} />
			</Stack>

			<Box sx={{ height: "auto", width: "100%" }}>
				<DataGrid
					loading={loading}
					rows={rows}
					columns={columns}
					disableColumnMenu
					disableColumnSorting
					disableColumnResize
					hideFooter
					unstable_heightStrategy="content"
					sx={dataGridSx}
				/>
			</Box>

			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
				<PaginationNavigation
					currentPage={currentPage}
					totalPages={totalPages}
					page={perPage}
					onPrev={handlePrev}
					onNext={handleNext}
					onChangePerPage={handlePerPage}
				/>
			</Box>

			<Box mb={2} mt={5}>
				<Divider />
			</Box>

			<Typography variant="h5" fontWeight={600} mb={3} ml={2}>
				Bonus History
			</Typography>

			<Box sx={{ height: "auto", width: "100%" }}>
				<DataGrid
					rows={historyRowsMapped}
					columns={historyGridColumns}
					disableColumnMenu
					disableColumnSorting
					disableColumnResize
					hideFooter
					unstable_heightStrategy="content"
					sx={dataGridSx}
				/>
			</Box>

			<ConfirmationModal
				open={confirmation.open}
				onClose={handleCloseConfirmation}
				onConfirm={handleConfirmAction}
				isLoading={approveRejectLoading}
				title={confirmation.action === "approved" ? "Approve Leadership Bonus?" : "Reject Leadership Bonus?"}
				description={
					confirmation.action === "approved"
						? `Are you sure you want to approve the $${confirmation.row?.bonus_amount?.toLocaleString() ?? 0} milestone?`
						: `Are you sure you want to reject the $${confirmation.row?.bonus_amount?.toLocaleString() ?? 0} milestone?`
				}
				confirmText={confirmation.action === "approved" ? "Approve Milestone" : "Reject Milestone"}
				confirmColor={confirmation.action === "approved" ? "#2E7D32 !important" : "#D32F2F !important"}
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleSnackbarClose}
					severity={snackbar.severity}
					sx={{ width: "100%", "& .MuiAlert-action button": { backgroundColor: "transparent" } }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
}
